/** What the Verify control is doing, if it is rendered at all. */
export type VerifyState =
  | { kind: 'hidden' }
  | { kind: 'offer' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'running' };

export interface IVerifyStateInput {
  /** Absent when this surface cannot start a verification at all. */
  canStart: boolean;
  /** When they last proved they hold the number, or null when nobody has. */
  verifiedAt: string | null;
  /** The number as typed. */
  value: string;
  /** Whether the typed number differs from the one on file. */
  isChanged: boolean;
  /** A verification already out on other handsets, or one this client just sent. */
  isRunning: boolean;
  /**
   * Why a verification cannot be started right now even though the number wants
   * one — a missing precondition this surface owns, such as the email address an
   * account is minted under. Null when nothing blocks it.
   */
  blockedReason: string | null;
}

/**
 * Which of the four states the Verify control is in.
 *
 * Pulled out of the component because the ordering is the whole rule and it is
 * the part worth pinning: `running` outranks `blocked` (a handshake already sent
 * cannot be un-sent by a precondition going stale), and an unsaved edit hides
 * the control rather than blocking it — verifying would prove the stored number
 * rather than the one on screen, and a grayed button would invite waiting for
 * something that is never going to become available.
 */
export function verifyState(input: IVerifyStateInput): VerifyState {
  if (!input.canStart || input.value.trim().length === 0) {
    return { kind: 'hidden' };
  }
  if (input.isRunning) {
    return { kind: 'running' };
  }
  if (input.verifiedAt || input.isChanged) {
    return { kind: 'hidden' };
  }
  return input.blockedReason ? { kind: 'blocked', reason: input.blockedReason } : { kind: 'offer' };
}
